import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { SERVICES } from "@/data/services";
import { getLenis } from "./SmoothScroll";
import { SITE, waLink } from "@/lib/config";
import { WaIcon } from "@/components/ui/WaIcon";
import { IgIcon } from "@/components/ui/IgIcon";

const LINKS = [
  { to: "/", label: "Beranda", id: "home" },
  { to: "/#cara-kerja", label: "Cara Kerja", id: "how" },
  { to: "/tentang-kami", label: "Tentang Kami", id: "about" },
  { to: "/artikel", label: "Artikel", id: "articles" },
  { to: "/faq", label: "FAQ", id: "faq" },
  { to: "/kontak", label: "Kontak", id: "contact" },
];

const navCls = ({ isActive }) =>
  `relative px-3 py-2 text-[14px] font-semibold transition-colors duration-300 ${isActive ? "text-accentb" : "text-slateb hover:text-accentb"}`;

const ServicesDropdown = ({ active }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => { document.removeEventListener("keydown", onKey); document.removeEventListener("mousedown", onClick); };
  }, []);
  return (
    <div ref={ref} className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        type="button"
        data-testid="nav-services-toggle"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-1 px-3 py-2 text-[14px] font-semibold transition-colors ${active ? "text-accentb" : "text-slateb hover:text-accentb"}`}
      >
        Layanan <ChevronDown size={15} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="absolute left-1/2 top-full w-[380px] -translate-x-1/2 pt-3"
            data-testid="nav-services-menu"
          >
            <div className="card overflow-hidden p-2 shadow-[0_30px_60px_-30px_rgba(51,78,104,.35)]">
              {SERVICES.map((s) => (
                <Link
                  key={s.slug}
                  to={`/layanan/${s.slug}`}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-service-${s.key}`}
                  className="group flex items-start gap-4 rounded-2xl p-4 transition-colors hover:bg-ice"
                >
                  <span className="font-display text-sm font-bold text-sky-brand">{s.index}</span>
                  <span className="flex-1">
                    <span className="block font-display text-[15px] font-bold text-slateb">{s.name}</span>
                    <span className="mt-1 block text-[13px] leading-snug text-ink-soft">{s.headline}</span>
                  </span>
                  <ArrowUpRight size={16} className="mt-1 text-accentb opacity-0 transition-opacity group-hover:opacity-100" />
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const MENU_MAIN = [
  { to: "/", label: "Beranda" },
  { to: "/#cara-kerja", label: "Cara Kerja" },
  { to: "/tentang-kami", label: "Tentang Kami" },
  { to: "/konsultan", label: "Tim Konsultan" },
  { to: "/artikel", label: "Artikel" },
  { to: "/faq", label: "FAQ" },
  { to: "/kontak", label: "Kontak" },
];

const panel = {
  closed: { clipPath: "circle(0px at calc(100% - 42px) 40px)", transition: { duration: 0.5, ease: [0.65, 0, 0.35, 1] } },
  open: { clipPath: "circle(150% at calc(100% - 42px) 40px)", transition: { duration: 0.75, ease: [0.65, 0, 0.35, 1] } },
};
const stagger = { open: { transition: { staggerChildren: 0.05, delayChildren: 0.25 } }, closed: {} };
const item = {
  closed: { y: "110%", opacity: 0 },
  open: { y: "0%", opacity: 1, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

const MobileMenu = ({ onClose }) => {
  const { pathname } = useLocation();
  const isActive = (to) => (to === "/" ? pathname === "/" : !to.includes("#") && pathname.startsWith(to));
  return (
    <motion.div
      initial="closed"
      animate="open"
      exit="closed"
      variants={panel}
      className="fixed inset-0 z-[60] flex flex-col overflow-y-auto bg-white lg:hidden"
      data-lenis-prevent
      data-testid="mobile-menu"
      role="dialog"
      aria-modal="true"
      aria-label="Menu navigasi"
    >
      <div className="grid-lines pointer-events-none absolute inset-0 opacity-40 [mask-image:linear-gradient(to_bottom,black,transparent_60%)]" aria-hidden="true" />
      <div className="wrap relative flex h-20 shrink-0 items-center justify-between">
        <Logo testId="mobile-menu-logo" />
        <button onClick={onClose} aria-label="Tutup menu" data-testid="mobile-menu-close" className="grid h-11 w-11 place-items-center rounded-full bg-slateb text-white transition-transform duration-300 active:scale-90">
          <X size={20} />
        </button>
      </div>

      <motion.nav variants={stagger} className="wrap relative mt-4 flex-1" aria-label="Menu seluler">
        <p className="eyebrow">Menu</p>
        <ul className="mt-4">
          {MENU_MAIN.map((l, i) => (
            <li key={l.to} className="overflow-hidden border-b border-line">
              <motion.div variants={item}>
                <Link to={l.to} onClick={onClose} data-testid={`mobile-link-${i}`} className="group flex items-center gap-4 py-3.5">
                  <span className={`w-6 text-[12px] font-bold ${isActive(l.to) ? "text-accentb" : "text-ink-soft"}`}>0{i + 1}</span>
                  <span className={`flex-1 font-display text-[26px] font-bold leading-tight tracking-[-0.02em] transition-colors ${isActive(l.to) ? "text-accentb" : "text-slateb group-active:text-accentb"}`}>{l.label}</span>
                  <ArrowUpRight size={18} className={`transition-transform duration-300 group-active:rotate-45 ${isActive(l.to) ? "text-accentb" : "text-sky-brand"}`} />
                </Link>
              </motion.div>
            </li>
          ))}
        </ul>

        <p className="eyebrow mt-9">Layanan</p>
        <div className="mt-4 grid grid-cols-3 gap-2.5">
          {SERVICES.map((s) => (
            <motion.div key={s.slug} variants={item}>
              <Link to={`/layanan/${s.slug}`} onClick={onClose} data-testid={`mobile-service-${s.key}`} className={`flex h-full flex-col justify-between rounded-2xl border p-3.5 transition-colors ${pathname.includes(s.slug) ? "border-sky-brand bg-ice" : "border-line bg-white"}`}>
                <span className="text-[11px] font-bold text-accentb">{s.index}</span>
                <span className="mt-6 font-display text-[15px] font-bold leading-tight text-slateb">{s.short}</span>
              </Link>
            </motion.div>
          ))}
        </div>
      </motion.nav>

      <motion.div variants={stagger} className="wrap relative shrink-0 pb-8 pt-8">
        <motion.div variants={item} className="grid grid-cols-2 gap-2.5">
          <Link to="/kontak#konsultasi" onClick={onClose} data-testid="mobile-cta" className="btn btn-slate !px-4">Konsultasi Gratis</Link>
          <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="mobile-wa" className="btn btn-sky !px-4"><WaIcon size={16} /> WhatsApp</a>
        </motion.div>
        <motion.div variants={item} className="mt-6 flex items-center justify-between gap-4 border-t border-line pt-5 text-[13px]">
          <a href={`mailto:${SITE.email}`} className="truncate font-semibold text-slateb" data-testid="mobile-email">{SITE.email}</a>
          <a href={SITE.instagramUrl} target="_blank" rel="noopener noreferrer" aria-label="Instagram Mika Solution" data-testid="mobile-instagram" className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-gradient-to-tr from-[#f9ce34] via-[#ee2a7b] to-[#6228d7] text-white">
            <IgIcon size={17} />
          </a>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    const l = getLenis();
    if (mobile) l?.stop(); else l?.start();
  }, [mobile]);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-50 transition-[background-color,box-shadow,backdrop-filter] duration-500 ${scrolled ? "bg-white/80 shadow-[0_10px_40px_-24px_rgba(51,78,104,.35)] backdrop-blur-xl" : "bg-transparent"}`}
    >
      <div className="wrap flex h-20 items-center justify-between gap-6">
        <Logo />
        <nav className="hidden items-center lg:flex" aria-label="Navigasi utama">
          <NavLink to="/" end className={navCls} data-testid="nav-home">Beranda</NavLink>
          <ServicesDropdown active={pathname.startsWith("/layanan")} />
          {LINKS.slice(1).map((l) => (
            <NavLink key={l.id} to={l.to} className={l.to.includes("#") ? navCls({ isActive: false }) : navCls} data-testid={`nav-${l.id}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <Link to="/kontak#konsultasi" data-testid="nav-cta" className="btn btn-slate hidden !py-3 sm:inline-flex">
            Konsultasi Gratis <ArrowUpRight size={16} />
          </Link>
          <button onClick={() => setMobile(true)} aria-label="Buka menu" data-testid="mobile-menu-open" className="grid h-11 w-11 place-items-center rounded-full border border-line bg-white text-slateb lg:hidden">
            <Menu size={20} />
          </button>
        </div>
      </div>
      {createPortal(<AnimatePresence>{mobile && <MobileMenu onClose={() => setMobile(false)} />}</AnimatePresence>, document.body)}
    </header>
  );
};
